import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as globals from '@wandb/weave/common/css/globals.styles';
import * as React from 'react';
import {useCallback, useRef} from 'react';
import {Button, Card} from 'semantic-ui-react';
import WaveSurfer from 'wavesurfer.js';

import {DD_PRIVACY_HIDDEN} from '../../../util/datadog/privacy';
import {useColorMode} from '../../../util/useColorMode';
import {THEME} from '../../common/css/color.styles';
import {formatDurationWithColons} from '../../common/util/time';

interface AudioViewerProps {
  ariaMetadata?: string; // Run metadata for screenreader aria-labels
  audioSrc?: string;
  caption?: string | null;
  height: number;
  mediaFailedToLoad?: boolean;
  headerElement?: React.ReactNode;
  failedLoadElement?: React.ReactNode;
  downloadFile: () => Promise<void>;
  trackSuccess?: () => void;
  trackError?: () => void;
  // Opt-in until other consumers are verified with it.
  redrawOnResize?: boolean;
}

const controlBarHeight = 30;

// Placeholder peaks: drawn onto a <canvas> overlay for immediate visual
// feedback, and passed to wavesurfer.load() so its MediaElement backend
// fires "ready" on canplay rather than waiting for a full file decode.
const NUM_PLACEHOLDER_PEAKS = 300;
const PLACEHOLDER_PEAKS = (() => {
  const data = new Float32Array(NUM_PLACEHOLDER_PEAKS);
  for (let i = 0; i < NUM_PLACEHOLDER_PEAKS; i++) {
    const t = i / NUM_PLACEHOLDER_PEAKS;
    data[i] = 0.3 + 0.7 * Math.abs(Math.sin(t * Math.PI * 5));
  }
  return data;
})();

function drawPlaceholderPeaks(canvas: HTMLCanvasElement, color: string) {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  const w = canvas.width;
  const h = canvas.height;
  const mid = h / 2;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = color;
  const barWidth = 1;
  const barGap = 1;
  const step = barWidth + barGap;
  const numBars = Math.floor(w / step);
  for (let i = 0; i < numBars; i++) {
    const peakIdx = Math.floor((i / numBars) * PLACEHOLDER_PEAKS.length);
    const barH = PLACEHOLDER_PEAKS[peakIdx] * (mid - 2);
    ctx.fillRect(i * step, mid - barH, barWidth, barH * 2);
  }
}

function getUrlPathname(url: string): string | null {
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

const AudioViewer = (props: AudioViewerProps) => {
  const {isDarkMode} = useColorMode();
  const {
    ariaMetadata,
    audioSrc,
    caption,
    height,
    mediaFailedToLoad,
    headerElement,
    failedLoadElement,
    downloadFile,
    redrawOnResize,
    trackSuccess,
    trackError,
  } = props;
  const wavesurferRef = useRef<WaveSurfer>(undefined);
  const waveformDomRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef(height);
  // oxlint-disable-next-line react/refs
  heightRef.current = height;
  const prevAudioSrcRef = useRef<string>(undefined);
  const trackSuccessRef = useRef(trackSuccess);
  // oxlint-disable-next-line react/refs
  trackSuccessRef.current = trackSuccess;
  const trackErrorRef = useRef(trackError);
  // oxlint-disable-next-line react/refs
  trackErrorRef.current = trackError;

  // Stabilize audioSrc: the parent may re-generate signed URLs on every
  // render (new token/signature in query params, same pathname). We only
  // want to re-initialize WaveSurfer when the actual file changes.
  const stableAudioSrc = React.useMemo(() => {
    if (!audioSrc) {
      return undefined;
    }
    const newPath = getUrlPathname(audioSrc);
    // oxlint-disable-next-line react/refs
    const oldPath = prevAudioSrcRef.current
      ? // oxlint-disable-next-line react/refs
        getUrlPathname(prevAudioSrcRef.current)
      : null;
    if (newPath && oldPath && newPath === oldPath) {
      // oxlint-disable-next-line react/refs
      return prevAudioSrcRef.current;
    }
    return audioSrc;
  }, [audioSrc]);

  // Keep the ref in sync after commit — avoids mutating refs during render.
  React.useEffect(() => {
    prevAudioSrcRef.current = stableAudioSrc;
  }, [stableAudioSrc]);

  const [audioLoading, setAudioLoading] = React.useState(true);
  const [audioPlaying, setAudioPlaying] = React.useState(false);
  const [audioTotalTime, setAudioTotalTime] = React.useState<number>();
  const [audioCurrentTime, setAudioCurrentTime] = React.useState<number>();
  const [placeholderState, setPlaceholderState] = React.useState<
    'loading' | 'fading' | 'done'
  >('loading');

  const placeholderCanvasRef = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (canvas) {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        drawPlaceholderPeaks(canvas, isDarkMode ? '#545d68' : '#ddd');
      }
    },
    [isDarkMode]
  );

  React.useEffect(() => {
    if (!stableAudioSrc) {
      return;
    }

    let wavesurfer: WaveSurfer | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let redrawId = 0;
    const abortController = new AbortController();

    // Use rAF to defer initialization until after the browser paints.
    // This ensures the container DOM ref is measured and avoids wasted
    // work if the parent re-renders before the first paint.
    const rafId = requestAnimationFrame(() => {
      if (!waveformDomRef.current) {
        return;
      }

      setPlaceholderState('loading');

      wavesurfer = WaveSurfer.create({
        backend: 'MediaElement',
        container: waveformDomRef.current,
        waveColor: globals.primary,
        progressColor: globals.darkBlue,
        cursorColor: globals.darkBlue,
        responsive: true,
        height: heightRef.current - controlBarHeight,
      });

      wavesurferRef.current = wavesurfer;

      wavesurfer.on('play', () => {
        setAudioPlaying(true);
      });
      wavesurfer.on('pause', () => {
        setAudioPlaying(false);
      });
      wavesurfer.on('ready', () => {
        const duration = wavesurfer!.getDuration();

        setAudioLoading(false);
        setAudioCurrentTime(undefined);
        setAudioTotalTime(duration || undefined);
        trackSuccessRef.current?.();

        // Fetch and decode the full audio to extract real waveform peaks.
        // The browser HTTP cache serves this instantly since WaveSurfer's
        // MediaElement backend already fetched the same URL.
        const fetchedUrl = stableAudioSrc;
        fetch(fetchedUrl, {signal: abortController.signal})
          .then(res => res.arrayBuffer())
          .then(buf => {
            const AudioCtx =
              window.AudioContext || (window as any).webkitAudioContext;
            const audioCtx = new AudioCtx();
            return audioCtx
              .decodeAudioData(buf)
              .finally(() => audioCtx.close());
          })
          .then(decoded => {
            if (wavesurferRef.current === wavesurfer) {
              (wavesurfer as any).backend.buffer = decoded;
              (wavesurfer as any).backend.setPeaks(null);
              wavesurfer!.drawBuffer();
              setPlaceholderState('fading');
            }
          })
          .catch(() => {
            if (wavesurferRef.current === wavesurfer) {
              setPlaceholderState('done');
            }
          });
      });
      wavesurfer.on('seek', () => {
        setAudioCurrentTime(wavesurfer!.getCurrentTime());
      });
      wavesurfer.on('audioprocess', () => {
        setAudioCurrentTime(wavesurfer!.getCurrentTime());
      });
      wavesurfer.on('error', () => {
        trackErrorRef.current?.();
      });

      wavesurfer.load(stableAudioSrc, [PLACEHOLDER_PEAKS] as any);

      // `responsive` only redraws on window resize, so containers that resize
      // on their own, like a drawer, would keep a stale waveform width.
      if (!redrawOnResize) {
        return;
      }
      const container = waveformDomRef.current;
      let lastWidth = container.clientWidth;
      resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(redrawId);
        redrawId = requestAnimationFrame(() => {
          if (container.clientWidth === lastWidth) {
            return;
          }
          lastWidth = container.clientWidth;
          wavesurfer?.drawBuffer();
        });
      });
      resizeObserver.observe(container);
    });

    return () => {
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(redrawId);
      resizeObserver?.disconnect();
      abortController.abort();
      if (wavesurfer) {
        wavesurfer.destroy();
        wavesurferRef.current = undefined;
      }
    };
  }, [redrawOnResize, stableAudioSrc]);

  React.useEffect(() => {
    if (wavesurferRef.current) {
      try {
        wavesurferRef.current.setHeight(height - controlBarHeight);
      } catch (_) {
        // noop
      }
    }
  }, [height]);

  return (
    <div
      className="media-card__wrapper"
      style={{
        width: '100%',
        height: '100%',
      }}>
      <Card
        className="audio-card content-card"
        style={{
          height: '100%',
          width: '100%',
        }}>
        {headerElement && (
          <Card.Content
            style={{
              border: 0,
              width: '100%',
              height: '100%',
              padding: '5px 0px',
              display: 'flex',
              alignItems: 'center',
            }}>
            {headerElement}
          </Card.Content>
        )}

        <div
          style={{
            position: 'relative',
            height: '100%',
            width: '100%',
            flex: '1 1 auto',
            display: mediaFailedToLoad ? 'none' : 'block',
          }}>
          <div
            className="audio-card-waveform"
            data-dd-privacy={DD_PRIVACY_HIDDEN}
            style={{
              width: '100%',
              height: '100%',
            }}
            ref={waveformDomRef}
          />
          {placeholderState !== 'done' && audioSrc && (
            <>
              <style>{`
                @keyframes audio-shimmer {
                  0%   { background-position: 0 0; }
                  100% { background-position: -200% 0; }
                }
              `}</style>
              <div
                onTransitionEnd={() => setPlaceholderState('done')}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  ...(placeholderState === 'fading'
                    ? {opacity: 0, transition: 'opacity 400ms ease-out'}
                    : {opacity: 1}),
                }}>
                <canvas
                  ref={placeholderCanvasRef}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background:
                      'repeating-linear-gradient(to right, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                    backgroundSize: '200% auto',
                    animation: 'audio-shimmer 2s infinite linear',
                  }}
                />
              </div>
            </>
          )}
        </div>
        {mediaFailedToLoad ? (
          failedLoadElement
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              width: '100%',
              height: `${controlBarHeight}px`,
            }}>
            <Button
              aria-label={`${audioPlaying ? 'Pause' : 'Play'} audio${
                ariaMetadata == null ? '' : ` for ${ariaMetadata}`
              }`}
              style={{
                flex: '0 0 auto',
                paddingTop: '0.5em',
                paddingBottom: '0.5em',
              }}
              disabled={audioLoading}
              loading={audioLoading}
              icon={audioLoading ? '' : audioPlaying ? 'pause' : 'play'}
              onClick={() => {
                if (wavesurferRef.current) {
                  wavesurferRef.current.playPause();
                }
              }}
            />
            <div
              style={{flex: '0 1 auto', overflow: 'hidden', marginLeft: '10px'}}
              className="audio-card-time">
              {[audioCurrentTime, audioTotalTime]
                .map(formatDurationWithColons)
                .join('/')}
            </div>
            {caption && (
              <div
                style={{
                  background: THEME.bgSurface,
                  width: '100%',
                  maxHeight: '3em',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  display: 'flex',
                  justifyContent: 'left',
                  flex: '1 1',
                  marginLeft: '10px',
                }}>
                {
                  <div style={{flexGrow: 1}}>
                    <div
                      style={{
                        padding: '0 5px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                      }}>
                      {caption}
                    </div>
                  </div>
                }
              </div>
            )}
            {audioSrc && (
              <Button
                aria-label={`Download audio${
                  ariaMetadata == null ? '' : ` for ${ariaMetadata}`
                }`}
                style={{
                  flex: '0 0 auto',
                  paddingTop: '0.5em',
                  paddingBottom: '0.5em',
                }}
                icon
                onClick={downloadFile}>
                <LegacyWBIcon name="download" />
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AudioViewer;
